import { normalizePhone } from '../services/firebase';

export interface WebParticipantIdentity {
    displayName: string;
    canonicalName: string;
    initials: string;
    photoURL?: string | null;
    isViewer: boolean;
    isOrganizer: boolean;
    isVerified?: boolean;
    isCleroPlus?: boolean;
}

const GENERIC_PARTICIPANT_NAMES = new Set([
    'friend',
    'participant',
    'someone',
    'a clero user',
    'clero user',
    'invitee',
    'guest',
    'you',
    'a participant',
    'a',
]);

export const isGenericParticipantName = (name: string): boolean => {
    const clean = String(name || '').trim().toLowerCase();
    if (!clean) return true;
    if (GENERIC_PARTICIPANT_NAMES.has(clean)) return true;
    if (/^friend(\s*\d+)?$/i.test(clean)) return true;
    return false;
};

export const getParticipantInitials = (name: string): string => {
    const clean = String(name || '').trim();
    if (!clean || isGenericParticipantName(clean)) return '•';
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '•';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const resolveWebParticipantIdentity = ({
    participant,
    fallbackName,
    viewerPhone,
    viewerDisplayName,
    viewerPhotoURL,
    organizerPhone,
    organizerName,
    organizerPhotoURL,
    session,
}: {
    participant?: any;
    fallbackName?: string | null;
    viewerPhone?: string;
    viewerDisplayName?: string;
    viewerPhotoURL?: string | null;
    organizerPhone?: string;
    organizerName?: string;
    organizerPhotoURL?: string | null;
    session?: any;
}): WebParticipantIdentity => {
    const rawPhone = String(participant?.phone || participant?.phoneSnapshot || participant?.id || participant?.key || '');
    const normPhone = normalizePhone(rawPhone);
    const normViewer = normalizePhone(viewerPhone || '');
    const normOrganizer = normalizePhone(organizerPhone || session?.organizerPhone || '');

    const isOrganizer = Boolean(
        participant?.isOrganizer === true ||
        rawPhone === 'organizer' ||
        (normOrganizer && (normPhone === normOrganizer || rawPhone === normOrganizer))
    );

    const isViewer = Boolean(
        participant?.isMe === true ||
        participant?.isViewer === true ||
        (normViewer && (normPhone === normViewer || rawPhone === normViewer))
    );

    // Resolve Candidate Names in Priority Order
    let candidateName = '';

    if (isOrganizer) {
        candidateName = String(
            organizerName ||
            session?.organizerName ||
            participant?.displayName ||
            participant?.name ||
            (normOrganizer && session?.participantsByPhone?.[normOrganizer]?.name) ||
            ''
        ).trim();
    } else if (isViewer) {
        candidateName = String(
            viewerDisplayName ||
            participant?.displayName ||
            participant?.name ||
            'You'
        ).trim();
    } else {
        // Participant candidate names
        const explicitName = String(
            participant?.displayName ||
            participant?.name ||
            participant?.fullName ||
            participant?.profileName ||
            ''
        ).trim();

        const shareName = String(fallbackName || '').trim();

        const byPhoneName = normPhone ? String(
            session?.participantsByPhone?.[normPhone]?.name ||
            session?.participantsByPhone?.[normPhone]?.displayName ||
            ''
        ).trim() : '';

        const summaryName = normPhone ? String(
            (Array.isArray(session?.participantSummaries)
                ? session.participantSummaries.find((s: any) => normalizePhone(s?.phone || s?.id) === normPhone)?.name
                : '') ||
            (Array.isArray(session?.invites)
                ? session.invites.find((i: any) => normalizePhone(i?.phone || i?.id) === normPhone)?.name
                : '') ||
            ''
        ).trim() : '';

        // Pick best non-generic name
        if (explicitName && !isGenericParticipantName(explicitName)) {
            candidateName = explicitName;
        } else if (shareName && !isGenericParticipantName(shareName)) {
            candidateName = shareName;
        } else if (byPhoneName && !isGenericParticipantName(byPhoneName)) {
            candidateName = byPhoneName;
        } else if (summaryName && !isGenericParticipantName(summaryName)) {
            candidateName = summaryName;
        } else {
            candidateName = explicitName || shareName || byPhoneName || summaryName || '';
        }
    }

    const canonicalName = isViewer && candidateName.toLowerCase() === 'you'
        ? (viewerDisplayName || 'You')
        : candidateName;

    // Resolve Photo URL
    const photoURL =
        participant?.photoURL ||
        participant?.photoUrl ||
        participant?.serverPhotoUri ||
        participant?.avatarUrl ||
        (isViewer ? viewerPhotoURL : null) ||
        (isOrganizer ? (organizerPhotoURL || session?.organizerPhotoURL || session?.organizerPhotoUrl) : null) ||
        (normPhone && session?.participantsByPhone?.[normPhone]?.photoURL) ||
        (normPhone && session?.participantsByPhone?.[normPhone]?.photoUrl) ||
        (normPhone && session?.coverageMap?.[normPhone]?.photoURL) ||
        (normPhone && session?.coverageMap?.[normPhone]?.photoUrl) ||
        null;

    const initials = getParticipantInitials(canonicalName || (normPhone ? '' : rawPhone));

    const isVerified = Boolean(
        participant?.isVerified ||
        participant?.isIdentityVerified ||
        (normPhone && session?.participantsByPhone?.[normPhone]?.isVerified)
    );

    const isCleroPlus = Boolean(
        participant?.isCleroPlus ||
        (normPhone && session?.participantsByPhone?.[normPhone]?.isCleroPlus)
    );

    const fallbackDisplayName = isOrganizer
        ? 'Organizer'
        : (isViewer ? 'You' : (rawPhone || 'Participant'));

    return {
        displayName: isViewer ? 'You' : (canonicalName || fallbackDisplayName),
        canonicalName: canonicalName || (isOrganizer ? 'Organizer' : 'Participant'),
        initials,
        photoURL,
        isViewer,
        isOrganizer,
        isVerified,
        isCleroPlus,
    };
};

export const buildParticipantOrderMap = ({
    session,
    invites,
    participants,
}: {
    session?: any;
    invites?: any[];
    participants?: any[] | Record<string, any>;
}): Map<string, number> => {
    const map = new Map<string, number>();
    let order = 0;

    map.set('organizer', order++);
    const orgPhone = normalizePhone(session?.organizerPhone || '');
    if (orgPhone) map.set(orgPhone, 0);

    const list = (Array.isArray(session?.participantSummaries) && session.participantSummaries.length > 0)
        ? session.participantSummaries
        : (Array.isArray(invites) && invites.length > 0 ? invites : (Array.isArray(session?.invites) ? session.invites : []));

    list.forEach((inv: any) => {
        const norm = normalizePhone(inv?.phone || inv?.id || '');
        if (norm && !map.has(norm)) {
            map.set(norm, order++);
        }
    });

    if (Array.isArray(participants)) {
        participants.forEach((p: any) => {
            const norm = normalizePhone(p?.phone || p?.normalizedPhone || p?.id || '');
            if (norm && !map.has(norm)) {
                map.set(norm, order++);
            }
        });
    } else if (participants && typeof participants === 'object') {
        Object.keys(participants).forEach((key) => {
            const norm = normalizePhone(key) || key;
            if (norm && !map.has(norm)) {
                map.set(norm, order++);
            }
        });
    }

    return map;
};

export const sortShareEntries = (entries: any[], orderMap: Map<string, number>): any[] => {
    return [...entries].sort((a, b) => {
        const aKey = String(Array.isArray(a) ? a[0] : (a?.key || a?.phone || a?.avatarPhone || ''));
        const bKey = String(Array.isArray(b) ? b[0] : (b?.key || b?.phone || b?.avatarPhone || ''));
        const aNorm = normalizePhone(aKey) || aKey;
        const bNorm = normalizePhone(bKey) || bKey;
        const aOrder = orderMap.has(aNorm) ? orderMap.get(aNorm)! : (orderMap.has(aKey) ? orderMap.get(aKey)! : 9999);
        const bOrder = orderMap.has(bNorm) ? orderMap.get(bNorm)! : (orderMap.has(bKey) ? orderMap.get(bKey)! : 9999);
        if (aOrder !== bOrder) return aOrder - bOrder;
        return aKey.localeCompare(bKey);
    });
};

